import { Component, OnDestroy, ViewChild, EventEmitter, Output, HostListener, ElementRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Subscription, BehaviorSubject } from 'rxjs';
import { debounceTime, mergeMap, tap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { ContactService } from '../../../TargetingShared/services/contact-service';
import { ContactModel } from '../../../TargetingShared/model/contact.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DiscoverRoutingService } from '../../../../Discover/DiscoverRouter/services/discover-routing.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { HandleTypeEnum } from '../../../../api/targeting/enum/handle-type.enum';
import { ScrollPanelComponent } from '../../../Basic/components/ScrollPanel/scroll-panel.component';
import { JournodexRegionsService } from '../../../Journodex/services/journodex-regions.service';
import { JournodexGeographyModel } from '../../../Journodex/models/journodex-geography.model';
import { HandleModel } from '../../../../Discover/Targeting/models/handle.model';
import { JournodexEnumsService } from '../../../../Discover/Core/services/journodex-enums.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { DiscoverRouteTypeEnum } from '../../../../Discover/DiscoverRouter/enums/discover-route-type.enum';
import { ChipModel } from '../../../Forms/models/chip.model';
import { ListPickerDialogComponent } from '../../../RhpContexts/components/ListPickerDialog/list-picker-dialog.component';
import { TargetingResultModel } from '../../../../Discover/Targeting/models/targeting-result.model';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { TargetingSearchesApiService } from '../../../../api/targeting/targeting-searches.api.service';
import { SortOrderEnum } from '../../../../api/shared/enums/sort-order.enum';
import { SearchModeEnum } from '../../../TargetingShared/enum/search-mode.enum';
import { OwnershipType } from '../../../../api/targeting/enum/ownership.type.enum';
import { CardMode } from '../ContactCard/contact-card.component';
import { RightSidebarComponent } from '../../../Layout/components/RightSidebar/right-sidebar.component';
import { TargetingList } from '../../../../Discover/Targeting/models/targeting-list.model';
import { LanguageModel } from '../../../../Core/Localization/models/language.model';
import { TargetingRhpDialogService } from '../../../TargetingShared/services/targeting-rhp-dialog.service';
import { TargetingSearchesService } from '../../../../Discover/Targeting/services/targeting-searches.service';
import { TargetingListList } from '../../../../Discover/Targeting/models/targeting-list-list.model';
import { TargetingListService } from '../../../../Discover/Targeting/services/targeting-list.service';
import { IncludeOrExclude } from '../../../Dialogs/services/data-select-dialog.service.interface';
import { JobRoleSelectDialogService } from '../../../../Discover/Targeting/services/jobrole-select-dialog.service';
import { TargetingSearchLanguageSelectDialogService } from '../../../TargetingShared/services/targeting-search-language-select-dialog.service';
import { DataSelectDialogModel } from '../../../Dialogs/models/data-select-dialog.model';
import { DataSelectDialogComponent } from '../../../Dialogs/components/DataSelectDialog/data-select-dialog.component';
import { JournodexSubjectParentModel } from '../../../Journodex/models/journodex.subject.model';
import { JobRoleModel } from '../../../../Discover/Core/models/job-role.model';
import { UserNoteModel } from '../../../Misc/models/user-note.model';
import { TargetingResultFactory } from '../../../../Discover/Targeting/factories/targeting-result.factory';
import { JournodexEntityFactory } from '../../../../Discover/Targeting/factories/journodex-entity.factory';
import { EmploymentFactory } from '../../../../Discover/Targeting/factories/employment.factory';
import { AutocompleteBrowseAllOptions } from './SubjectChips/subject-chips.component';
import { promiseOnlyResolved } from '../../../../Core/utils';
import { LanguageTagEnum } from '../../../../Core/Localization/enums/language-tag.enum';
import { AutocompleteChipsOptions } from '../../../Forms/components/AutocompleteChips/autocomplete-chips.component';
import { FocusHelper } from '../../../Forms/helpers/focus.helper';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { ContactEditBannerErrorTypeEnum, bannerErrorsMap } from '../../../../Common/RhpContexts/enums/contact-edit-banner-error-type.enum';
import { BannerErrorConfigModel } from '../../../../Common/Misc/models/banner-error-config.model';
import { OutletModel } from '../../../../Discover/Targeting/models/outlet.model';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { SubjectDialogComponent } from '../../../../Discover/Targeting/components/TargetingSearch/SubjectDialog/subject-dialog.component';


@Component({
    selector: 'agpr-contact-editor',
    templateUrl: './contact-editor.component.html',
    styleUrls: ['./contact-editor.component.scss'],
    providers: [
        TargetingSearchLanguageSelectDialogService,
        JobRoleSelectDialogService,
    ],
    standalone: false
})
export class ContactEditorComponent implements OnDestroy {

    @Output() hideContactEditor = new EventEmitter<string>(null);

    @ViewChild(ScrollPanelComponent) scrollPanel: ScrollPanelComponent;
    @ViewChild('rhp') rhp: RightSidebarComponent;

    contact: ContactModel;
    loading: boolean = false;

    geoSearchResults: JournodexGeographyModel[];

    listChips: ChipModel[] = [];
    listIds: number[] = [];

    overlayMode: boolean = true;

    usersMap: {[key: number]: string} = [];
    currentUser: UserModel;

    sidebarExpanded: boolean = false;
    rhpTakeOver: boolean = false;
    bannerErrorList: BannerErrorConfigModel[] = [];
    isShowBannerMessage: boolean = false;
    isBannerExpanded: boolean = false;

    noteText: string = '';
    editingNote: UserNoteModel;
    matchedSystemContacts: TargetingResultModel[] = [];
    searchEntity: TargetingResultModel;

    helpLink: string = 'https://help.agilitypr.com/en/articles/5409425-how-do-i-add-private-contacts-to-my-agility-account';

    firstNameFormControl: FormControl<string>;
    lastNameFormControl: FormControl<string>;
    emailFormControl: FormControl<HandleModel[]>;
    contactEditorForm: FormGroup = new FormGroup({});

    private oldTitle: string;
    private subscriptions: Subscription[] = [];

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly handleTypeEnum = HandleTypeEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly cardModes = CardMode;
    readonly agprComponentStyleEnum = AgprComponentStyleEnum;
    readonly browseAllButtonSpec = {
        icon: 'fa-regular fa-duotone fa-binoculars',
        tooltip: 'Monitoring.search.browseAll',
    };
    readonly languagesAutocompleteOptions: AutocompleteChipsOptions = {
        suggestionsFetcher: this.localizationService.autocompleteLanguages.bind(this.localizationService),
        delay: 300,
        minLength: 3,
        forceSelection: true,
    };

    constructor(
        public coreDialogService: CoreDialogService,
        public contactService: ContactService,
        public discoverRoutingService: DiscoverRoutingService,
        public journodexRegionsService: JournodexRegionsService,
        public journodexEnumsService: JournodexEnumsService,
        public localizationService: LocalizationService,
        public targetingSearchesService: TargetingSearchesService,
        public messageService: MessageService,
        public targetingRhpDialogService: TargetingRhpDialogService,
        private targetingListService: TargetingListService,
        private targetingSearchesApiService: TargetingSearchesApiService,
        private currentUserService: CurrentUserStateService,
        private languageSelectDialogService: TargetingSearchLanguageSelectDialogService,
        private jobRoleSelectDialogService: JobRoleSelectDialogService,
        private journodexEntityFactory: JournodexEntityFactory,
        private employmentFactory: EmploymentFactory,
        private targetingResultFactory: TargetingResultFactory,
        public titleService: Title,
        private el: ElementRef,
    ) {
        this.oldTitle = this.titleService.getTitle();
        this.loading = true;
        this.subscriptions.push(
            this.discoverRoutingService.getCurrentRoute().subscribe(route => {
                if (route.type === DiscoverRouteTypeEnum.contactEditor) {
                    this.overlayMode = false;
                    if (route.value) {
                        this.contactService.setEditingContact(route.value);
                    } else {
                        this.contactService.createBlank();
                    }
                } else {
                    this.overlayMode = true;
                }
            }),
            this.currentUserService.getAccountUsers().subscribe(
                users => {
                    this.usersMap = {};

                    if (users) {
                        users.forEach(user => {
                            this.usersMap[user.id] = user.getDisplayName();
                        });
                    }
                }
            ),
            this.currentUserService.getUser().subscribe(x => this.currentUser = x),
            this.currentUserService.getUILanguageTag().subscribe(tag => {
                switch (tag) {
                    case LanguageTagEnum.fr_FR:
                    case LanguageTagEnum.fr_CA:
                        this.helpLink = 'https://help.agilitypr.com/en/articles/5430406-comment-ajouter-des-contacts-prives';
                        break;
                    default:
                        this.helpLink = 'https://help.agilitypr.com/en/articles/5409425-how-do-i-add-private-contacts-to-my-agility-account';
                }
            }),
            this.contactService.getEditingContact().subscribe(contact => {
                if (contact) {
                    this.loading = false;
                    this.contact = contact.clone();
                    this.contact.employmentModels = [];
                    if (!this.contact.selectedEmployment) {
                        this.contact.selectedEmployment = this.employmentFactory.createEmpty();
                    }
                    if (contact.listMemberships) {
                        this.listChips = contact.listMemberships.map(x => {
                            return {
                                label: x.name
                            };
                        });
                        this.listIds = contact.listMemberships.map(x => x.id);
                    }
                    this.setUpContactEditorForm(this.contact);

                    this.findMatchingContacts();
                    this.titleService.setTitle(
                        this.localizationService.transform(
                            this.contact.id ? 'Discover.editContact.browserTitle' : 'Discover.createContact.browserTitle'
                        )
                    );
                }
            }),
            this.contactService.getFocusedRhpEntity().subscribe(entity => this.searchEntity = entity),
        );
    }

    @HostListener('window:keyup.escape', ['$event']) onKeyDown(e: KeyboardEvent) {
        if (this.coreDialogService.hasOpenedDialogs()) {
            return;
        }
        this.hideContactEditor.emit(null);
    }

    setUpContactEditorForm(contact: ContactModel): void {
        this.firstNameFormControl = new FormControl<string>(contact.firstName, [ Validators.required, Validators.maxLength(33) ]);
        this.lastNameFormControl = new FormControl<string>(contact.lastName, [ Validators.required, Validators.maxLength(33) ]);
        this.emailFormControl = new FormControl<HandleModel[]>(this.getFilteredHandles(contact.handles, HandleTypeEnum.EMAIL), [ Validators.required ]);
        this.contactEditorForm = new FormGroup({
            primaryInformationForm: new FormGroup({
                firstName: this.firstNameFormControl,
                lastName: this.lastNameFormControl,
                email: this.emailFormControl
            }),
            socialMediaForm: new FormGroup({
                twitter: new FormControl<HandleModel[]>(this.getFilteredHandles(contact.handles, HandleTypeEnum.TWITTER)),
                facebook: new FormControl<HandleModel[]>(this.getFilteredHandles(contact.handles, HandleTypeEnum.FACEBOOK)),
                linkedin: new FormControl<HandleModel[]>(this.getFilteredHandles(contact.handles, HandleTypeEnum.LINKEDIN)),
                instagram: new FormControl<HandleModel[]>(this.getFilteredHandles(contact.handles, HandleTypeEnum.INSTAGRAM)),
                bluesky: new FormControl<HandleModel[]>(this.getFilteredHandles(contact.handles, HandleTypeEnum.BLUESKY)),
                tiktok: new FormControl<HandleModel[]>(this.getFilteredHandles(contact.handles, HandleTypeEnum.TIKTOK)),
                youtube: new FormControl<HandleModel[]>(this.getFilteredHandles(contact.handles, HandleTypeEnum.YOUTUBE))
            }),
            generalInformationForm: new FormGroup({
                phone: new FormControl<HandleModel[]>(this.getFilteredHandles(contact.handles, HandleTypeEnum.PHONE)),
                website: new FormControl<HandleModel[]>(this.getFilteredHandles(contact.handles, HandleTypeEnum.WEBSITE)),
                subjects: new FormControl<JournodexSubjectParentModel[]>(contact.subjects),
                bio: new FormControl<string>(contact.bio),
                language: new FormControl<LanguageModel[]>(contact.languages)
            }),
            addressForm: new FormGroup({
                addressLine1: new FormControl<string>(contact.addressLine1),
                addressLine2: new FormControl<string>(contact.addressLine2),
                geography: new FormControl<JournodexGeographyModel>(contact.geography),
                postalCode: new FormControl<string>(contact.postalCode)
            }),
            employmentForm: new FormGroup({
                jobTitle: new FormControl<string>(contact.selectedEmployment.jobTitle),
                jobRoles: new FormControl<JobRoleModel[]>(contact.selectedEmployment.jobRoles),
                outlet: new FormControl<OutletModel>(contact.selectedEmployment.outlet)
            }),
            notes: new FormControl<UserNoteModel[]>(contact.userNotes)
        });

        this.subscriptions.push(
            this.firstNameFormControl.valueChanges.pipe(debounceTime(300)).subscribe(changes => {
                this.contact.firstName = changes;
                this.findMatchingContacts();
            }),
            this.lastNameFormControl.valueChanges.pipe(debounceTime(300)).subscribe(changes => {
                this.contact.lastName = changes;
                this.findMatchingContacts();
            }),
            this.emailFormControl.valueChanges.subscribe(changes => {
                this.updateHandles(changes, HandleTypeEnum.EMAIL);
                this.findMatchingContacts();
            }),
            this.contactEditorForm.get('socialMediaForm').valueChanges.subscribe(changes => {
                this.updateHandles(changes.twitter, HandleTypeEnum.TWITTER);
                this.updateHandles(changes.facebook, HandleTypeEnum.FACEBOOK);
                this.updateHandles(changes.linkedin, HandleTypeEnum.LINKEDIN);
                this.updateHandles(changes.instagram, HandleTypeEnum.INSTAGRAM);
                this.updateHandles(changes.bluesky, HandleTypeEnum.BLUESKY);
                this.updateHandles(changes.tiktok, HandleTypeEnum.TIKTOK);
                this.updateHandles(changes.youtube, HandleTypeEnum.YOUTUBE);
            }),
            this.contactEditorForm.get('generalInformationForm').valueChanges.subscribe(changes => {
                this.updateHandles(changes.phone, this.handleTypeEnum.PHONE);
                this.updateHandles(changes.website, this.handleTypeEnum.WEBSITE);
                this.contact.subjects = changes.subjects;
                this.contact.bio = changes.bio;
                this.contact.languages = changes.language;
            }),
            this.contactEditorForm.get('addressForm').valueChanges.subscribe(changes => {
                this.contact.addressLine1 = changes.addressLine1;
                this.contact.addressLine2 = changes.addressLine2;
                this.contact.geography = changes.geography;
                this.contact.postalCode = changes.postalCode;
            }),
            this.contactEditorForm.get('employmentForm').valueChanges.subscribe(changes => {
                this.contact.selectedEmployment.jobTitle = changes.jobTitle;
                this.contact.selectedEmployment.jobRoles = changes.jobRoles;
                this.contact.selectedEmployment.outlet = changes.outlet;
            }),
            this.contactEditorForm.get('notes').valueChanges.subscribe(changes => {
                this.contact.userNotes = changes;
            })
        );
    }

    private getFilteredHandles(handles: HandleModel[] = [], type: HandleTypeEnum): HandleModel[] {
        return handles.filter(handle => handle.handleType === type);
    }

    private updateHandles(handles: HandleModel[], type: HandleTypeEnum): void {
        this.contact.handles = this.contact.handles.filter(handle => handle.handleType !== type);
        this.contact.handles.push(...handles);
    }

    subjectsBrowseAllClicked(options?: AutocompleteBrowseAllOptions) {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                selectedSubjects: this.contact.subjects || [],
                initialFilterString: options?.query,
                scrollToParentId: options?.parent?.id,
            },
            outputs: {
                dialogClose: (selectedSubjects: JournodexSubjectParentModel[]) => {
                    if (selectedSubjects === null) {
                        return;
                    }

                    this.contactEditorForm.get('generalInformationForm.subjects').setValue(selectedSubjects.slice());
                }
            }
        };

        this.coreDialogService.open(SubjectDialogComponent, io);
    }

    languagesBrowseAllClicked() {
        this.languageSelectDialogService.prepareData(IncludeOrExclude.include, this.contact.languages.map(l => l.id));
        const io: DataSelectDialogModel = {
            inputs: {
                id: 'targetingSearch.languageSelectDialog',
                dialogService: this.languageSelectDialogService,
                dialogHeader: 'Monitoring.result.languages',
                dialogMessage: 'Monitoring.result.selectLanguagesMsg',
                leftColumnHeader: 'Monitoring.result.availableLanguages',
                rightColumnHeader: 'Monitoring.result.selectedLanguages',
            },
            outputs: {
                dialogClose: (selectedLanguages: LanguageModel[]) => {
                    this.contactEditorForm.get('generalInformationForm.language').setValue(selectedLanguages.slice());
                }
            }
        };
        this.coreDialogService.open(DataSelectDialogComponent, io);
    }

    jobRolesBrowseAllClicked() {
        const jobRoleIds = this.contact.selectedEmployment.jobRoles.map(jobRole => jobRole.id);
        this.jobRoleSelectDialogService.prepareData(IncludeOrExclude.include, jobRoleIds);
        const io: DataSelectDialogModel = {
            inputs: {
                id: 'targetingSearch.jobRoleSelectDialog',
                dialogService: this.jobRoleSelectDialogService,
                dialogHeader: 'Discover.dialogs.browsers.jobrole.header',
                dialogMessage: 'Discover.dialogs.browsers.jobrole.message',
                leftColumnHeader: 'Discover.dialogs.browsers.jobrole.available',
                rightColumnHeader: 'Discover.dialogs.browsers.jobrole.selected',
            },
            outputs: {
                dialogClose: (selectedJobRoles: JobRoleModel[]) => {
                    this.contactEditorForm.get('employmentForm.jobRoles').setValue(selectedJobRoles.slice());
                }
            }
        };
        this.coreDialogService.open(DataSelectDialogComponent, io);
    }

    editContact() {
        if (this.contactEditorForm.valid) {
            this.loading = true;
            this.contactService.editContact(this.contact, this.listIds).subscribe({
                next: () => {
                    this.hideContactEditor.emit(this.contact.id);
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                }
            });
        } else {
            this.setBannerErrorList();
            FocusHelper.focusFirstError(this.el.nativeElement, '.agpr-form-message', this.scrollPanel);
        }
    }

    createContact() {
        if (this.contactEditorForm.valid) {
            this.loading = true;
            this.contactService.createContact(this.contact, this.listIds, this.overlayMode).subscribe({
                next: () => {
                    this.hideContactEditor.emit(this.contact.id);
                    this.loading = false;
                },
                error: () => this.loading = false
            });
        } else {
            this.setBannerErrorList();
            FocusHelper.focusFirstError(this.el.nativeElement, '.agpr-form-message', this.scrollPanel);
        }
    }

    setBannerErrorList() {
        this.scrollPanel.scrollTo(0, 0, true);
        const errorArray = [];
        if (this.firstNameFormControl.errors?.['required']) {
            errorArray.push(bannerErrorsMap.get(ContactEditBannerErrorTypeEnum.CONTACT_EDITOR_FIRSTNAME_ERROR));
        }
        if (this.lastNameFormControl.errors?.['required']) {
            errorArray.push(bannerErrorsMap.get(ContactEditBannerErrorTypeEnum.CONTACT_EDITOR_LASTNAME_ERROR));
        }
        if (this.emailFormControl.errors?.['required']) {
            errorArray.push(bannerErrorsMap.get(ContactEditBannerErrorTypeEnum.CONTACT_EDITOR_EMAIL_ERROR));
        }
        this.bannerErrorList = errorArray;
        this.isShowBannerMessage = true;
    }

    isMessageExpanded(event: boolean) {
        this.isBannerExpanded = !event;
    }

    focusErrorElement(errorId: string) {
        requestAnimationFrame(() => FocusHelper.focusError(errorId));
    }

    /**
     * We always overwrite the current outlet when autocomplete string is changed.
     * When item is selected, the string version of the outlet is overwritten with the
     * actual object. This is due to how autocomplete works at the moment; and because
     * we accept outlets that don't exist in the B-E, just as string names
     * @param outletName value from autocomplete
     */
    outletChanged(outletName: string) {
        if (!this.contact.selectedEmployment) {
            this.contact.selectedEmployment = this.employmentFactory.createEmpty();
        }
        this.contact.selectedEmployment.outlet = this.journodexEntityFactory.createEmptyOutlet();
        this.contact.selectedEmployment.outlet.name = outletName;
        this.contactService.updateDirtyFlag();
    }

    findMatchingContacts() {
        // We are only searching on names at the moment, no emails
        const contactName = this.contact.firstName + ' ' + this.contact.lastName;
        if (contactName.trim().length > 0 && this.contact.lastName && this.contact.firstName) {
            this.targetingSearchesApiService.performSearch(
                {
                    searchMode: SearchModeEnum.contact,
                    ownershipTypes: [OwnershipType.SYSTEM],
                    contactIdNamePair: {
                        include: [{ name: contactName, id: null }],
                        exclude: []
                    }
                },
                0,
                5,
                SortOrderEnum.ascending,
                'LAST_NAME'
            ).pipe(
                tap(async results => {
                    const resultPromises = results.results.map(x => this.targetingResultFactory.createTargetingResult(x));
                    this.matchedSystemContacts = await promiseOnlyResolved(resultPromises);
                }),
                mergeMap(() => {
                    return this.targetingListService.getListMemberships(this.matchedSystemContacts.map(x => ({ contactUuid: x.id })));
                })
            ).subscribe(listMemberships => {
                for (const contact of this.matchedSystemContacts) {
                    const listMembership = listMemberships.find(memberships => memberships.uuid === contact.id);
                    if (listMembership) {
                        contact.listMemberships = listMembership.targetingLists.map(x => new TargetingList(x));
                    }
                }
            });
        }
    }

    openListPicker() {
        // TODO : make list picker work with both targetingresults and full models
        // via base class or other mechanism?
        const convertedEntity: TargetingResultModel = this.targetingResultFactory.createEmptyTargetingResult();

        convertedEntity.entityType = EntityType.CONTACT;
        convertedEntity.id = this.contact.id;
        convertedEntity.listMemberships = this.contact.listMemberships;
        convertedEntity.selectedEmployment = this.contact.selectedEmployment;

        this.coreDialogService.open(ListPickerDialogComponent, {
            inputs: {
                showListBuilderCheckbox: false,
                addMode: true,
                addImmediately: false,
                addHocAddEntity: convertedEntity
            },
            outputs: {
                // The changes done in this editor do not immediately reflect in the search results
                newSelectedLists: (lists: TargetingListList[]) => {
                    lists.forEach(list => {
                        const chip = {
                            label: list.name
                        };
                        if (!this.listChips.find(x => x.label === chip.label)) {
                            this.listChips.push(chip);
                            this.listIds.push(list.id);
                        }
                    });
                    // forcing the ngOnChanges to run by creating new reference
                    this.listChips = [...this.listChips];
                    this.contactService.updateDirtyFlag();
                }
            }
        });
    }

    unselectListChip(index) {
        this.listIds.splice(index, 1);
        this.listChips.splice(index, 1);
        this.contactService.updateDirtyFlag();
    }

    contactExpanded(entity: TargetingResultModel): void {
        let outletId = null;
        if (entity.selectedEmployment && entity.selectedEmployment.outlet) {
            outletId = entity.selectedEmployment.outlet.id;
        }
        this.targetingRhpDialogService.openTakeOver(
            {
                inputs: {
                    contactId: entity.id,
                    outletId: outletId,
                    entityType : EntityType.CONTACT,
                    isPrivate: false
                }
            }
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.titleService.setTitle(this.oldTitle);
    }
}
